/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import { EditingState, DataTypeProvider, SummaryState, IntegratedSummary } from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditColumn,
  TableInlineCellEditing,
  TableColumnResizing,
  TableSummaryRow
} from '@devexpress/dx-react-grid-material-ui';
import ReactSelect from 'react-select';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import itemService from '../../../services/ItemService';
import { getFormattedItemRecords } from '../../MyTime/table/helpers';
import AsyncSelect from 'react-select/async';

const getRowId = (row) => row.id;

const FocusableCell = ({ onClick, ...restProps }) => <Table.Cell {...restProps} tabIndex={0} onFocus={onClick} />;

const CurrencyFormatter = ({ value }) =>
  value ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 0;
const CurrencyTypeProvider = (props) => <DataTypeProvider formatterComponent={CurrencyFormatter} {...props} />;

const LookupEditor = ({ value, onValueChange, column, onBlur, availableColumnValues }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const columnName = column.name;
  const isClassRef = columnName === 'ClassRef';

  // Predefined options for ClassRef
  const classRefOptions = availableColumnValues['ClassRef']?.map((item) => ({
    value: item.name || item,
    label: item.name || item,
    id: item.value || item
  }));

  const loadOptions = async (inputValue) => {
    try {
      let queryParams;
      if (inputValue?.length > 3 && onBlur) {
        queryParams = {
          take: 100,
          skip: 100,
          queryString: inputValue
        };

        const itemRefs = await itemService.getItemsFromQBO(queryParams);

        if (itemRefs?.data?.data?.QueryResponse?.Item?.length > 0) {
          const formattedItemRefs = getFormattedItemRecords(itemRefs.data.data.QueryResponse.Item);

          const options = formattedItemRefs?.map((item) => ({
            value: item.name || item,
            label: item.name || item,
            id: item.value || item,
            Description: item.Description || '',
            ClassRef: item.ClassRef || ''
          }));

          return options;
        }
      }
    } catch (error) {
      return [];
    }
  };

  // Determine the options to use based on columnName
  const options = isClassRef ? classRefOptions : [];

  // const debouncedLoadOptions = useDebounce(loadOptions, 1000); // Adjust the debounce delay as needed

  const dropdownValue = { value, label: value };
  return (
    <>
      {isClassRef ? (
        <ReactSelect
          onBlur={isSmall ? null : onBlur} //blur doesn't work in mobile phones
          className="basic-single"
          classNamePrefix="select"
          defaultValue={dropdownValue}
          isSearchable={true}
          name="ClassRef"
          options={options}
          // eslint-disable-next-line no-undef
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          onChange={(selectedOption) => {
            onValueChange({
              name: selectedOption.value,
              value: selectedOption.id
            });
          }}
        />
      ) : (
        <AsyncSelect
          onBlur={isSmall ? null : onBlur} //blur doesn't work in mobile phones
          className="basic-single"
          classNamePrefix="select"
          defaultValue={dropdownValue}
          isSearchable={true}
          name="ItemRef"
          loadOptions={loadOptions} // Use the loadOptions function for async loading
          // eslint-disable-next-line no-undef
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          onChange={(selectedOption) => {
            if (columnName === 'ItemRef') {
              onValueChange({
                name: selectedOption.value,
                value: selectedOption.id
                // Description: item.Description || '',
                // ClassRef: item.ClassRef || ''
              });
            } else {
              onValueChange(selectedOption.value);
            }
          }}
        />
      )}
    </>
  );
};

const LookupFormatter = ({ availableColumnValues, ...props }) => (
  <DataTypeProvider
    editorComponent={(params) => <LookupEditor availableColumnValues={availableColumnValues} {...params} />}
    {...props}
  />
);

const AddExpenseDialogTable = ({ itemRefs, classRefs, rows, setRows }) => {
  const [columns] = useState([
    {
      name: 'ItemRef',
      title: 'Item',
      getCellValue: (row) => (row?.ItemRef ? row?.ItemRef.name : '')
    },

    { name: 'Description', title: 'Description' },
    {
      name: 'ClassRef',
      title: 'Class',
      getCellValue: (row) => (row?.ClassRef ? row?.ClassRef.name : '')
    },
    { name: 'Qty', title: 'Qty' },
    { name: 'Price', title: 'Price' }
  ]);

  const [columnWidths, setColumnWidths] = useState([
    { columnName: 'ItemRef', width: 180 },
    { columnName: 'Description', width: 180 },
    { columnName: 'Qty', width: 120 },
    { columnName: 'Price', width: 120 },
    { columnName: 'ClassRef', width: 180 }
  ]);

  const [lookupColumns] = useState(['ItemRef', 'ClassRef']);

  const [editingCells, setEditingCells] = useState([]);
  const [tableColumnExtensions] = useState([{ columnName: 'Price', align: 'right' }]);
  const [totalSummaryItems] = useState([{ columnName: 'Price', type: 'sum' }]);
  const [currencyColumns] = useState(['Price']);

  const commitChanges = ({ added, changed, deleted }) => {
    let changedRows;

    if (added) {
      const startingAddedId = rows.length > 0 ? Math.max(rows[rows.length - 1].id, rows[0].id) + 1 : 0;
      changedRows = [
        ...added.map((row, index) => ({
          id: startingAddedId + index,
          ...row
        })),
        ...rows
      ];
      setEditingCells([{ rowId: startingAddedId, columnName: columns[0].name }]);
    }
    if (changed) {
      // if (changed[rows.length - 1]?.ItemRef) {
      //   changed[rows.length - 1].Description = changed[rows.length - 1].ItemRef.Description;
      //    changed[rows.length - 1].ClassRef = changed[rows.length - 1].ItemRef.ClassRef;
      // }

      //check if changed contains ItemRef or ClassRef or not
      //if contains update all rows:
      const changedRowId = Object.keys(changed)[0]; // Get the first changed row ID

      // Extract ItemRef and ClassRef values
      const changedRowIdHasItemRef = changedRowId ? changed[changedRowId]?.ItemRef : null;
      const changedRowIdHasClassRef = changedRowId ? changed[changedRowId]?.ClassRef : null;

      if (changedRowIdHasItemRef || changedRowIdHasClassRef) {
        // Apply ItemRef and ClassRef to ALL rows
        changedRows = rows.map((row) => ({
          ...row,
          ...(changedRowIdHasItemRef ? { ItemRef: changedRowIdHasItemRef } : {}),
          ...(changedRowIdHasClassRef ? { ClassRef: changedRowIdHasClassRef } : {})
        }));
      } else {
        // Apply changes only to specific rows that exist in 'changed'
        changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed[row.id] } : row));
      }
    }
    if (deleted) {
      // eslint-disable-next-line no-undef
      const deletedSet = new Set(deleted);
      changedRows = rows.filter((row) => !deletedSet.has(row.id));
    }

    setRows(changedRows);
  };

  const addEmptyRow = () => commitChanges({ added: [{}] });

  const availableColumnValues = {
    ItemRef: itemRefs,
    ClassRef: classRefs
  };

  return (
    <Paper>
      <Grid rows={rows} columns={columns} getRowId={getRowId}>
        <LookupFormatter for={lookupColumns} availableColumnValues={availableColumnValues} />
        <CurrencyTypeProvider for={currencyColumns} />
        <SummaryState totalItems={totalSummaryItems} />
        <IntegratedSummary />

        <EditingState
          onCommitChanges={commitChanges}
          editingCells={editingCells}
          onEditingCellsChange={setEditingCells}
          addedRows={[]}
          onAddedRowsChange={addEmptyRow}
        />
        <Table cellComponent={FocusableCell} columnExtensions={tableColumnExtensions} />
        <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
        <TableHeaderRow />
        <TableInlineCellEditing selectTextOnEditStart />
        <TableEditColumn showAddCommand showDeleteCommand />
        <TableSummaryRow />
      </Grid>
    </Paper>
  );
};

export default AddExpenseDialogTable;
